'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

interface StatusBadgeProps {
  status: 'UP' | 'DOWN' | 'PAUSED' | 'EXPIRING-DOMAIN' |   'EXPIRING-SSL' ;
  size?: 'sm' | 'md' | 'lg';
  isText?: boolean;
}

const Badge = styled.div<{ $status: string; $size: string }>`
  width: ${props => props.$size === 'sm' ? '32px' : props.$size === 'md' ? '40px' : '48px'};
  height: ${props => props.$size === 'sm' ? '32px' : props.$size === 'md' ? '40px' : '48px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => {
    switch (props.$status) {
      case 'UP':
        return theme.colors.status.success;
      case 'DOWN':
        return theme.colors.status.error;
      case 'PAUSED':
        return theme.colors.gray[500];
      case 'expiring-domain':
      case 'expiring-ssl':
        return theme.colors.status.warning;
      case 'not-started':
        return theme.colors.status.info;
      default:
        return theme.colors.gray[500];
    }
  }};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Icon = styled.svg`
  width: 60%;
  height: 60%;
  color: ${theme.colors.white};
`;

const StatusText = styled.span<{ $status: string }>`
  font-size: 14px;
  font-weight: 400;
  color: ${props => {
    switch (props.$status) {
      case 'UP':
        return theme.colors.status.successAlt;
      case 'DOWN':
        return theme.colors.status.errorAlt;
      case 'PAUSED':
        return theme.colors.gray.neutral;
      case 'EXPIRING-DOMAIN':
      case 'EXPIRING-SSL':
        return theme.colors.status.warning;
      default:
        return theme.colors.gray[800];
    }
  }};
`;

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md', isText = false }) => {
  const renderContent = () => {
    switch (status) {
      case 'UP':
        if (isText) {
          return 'Up';
        }
        return (
          <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </Icon>
        );
      case 'DOWN':
        if (isText) {
          return 'Down';
        }
        return (
          <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </Icon>
        );
      case 'PAUSED':
        if (isText) {
          return 'Paused';
        }
        return (
          <Icon viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" />
            <rect x="14" y="5" width="4" height="14" />
          </Icon>
        );
      case 'EXPIRING-DOMAIN':
        if (isText) {
          return 'Expiring Domain';
        }
      case 'EXPIRING-SSL':

        if (isText) {
          return 'Expiring SSL';
        }
        return (
          <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </Icon>
        );
      default:
        return null;
    }
  };

  return (
   isText ? (
    <StatusText $status={status}>{renderContent()}</StatusText>
   ) : (
    <Badge $status={status} $size={size}>
      {renderContent()}
    </Badge>
  ))
};

export default StatusBadge;

